import { Observable } from "rxjs";
import { WSPublicChannel, WSNamespaces } from "./ws-client.js";
const publicEventsIds = [
  WSPublicChannel.sportbook,
  WSPublicChannel.exchangeRates,
  WSPublicChannel.oddsBooster,
  WSPublicChannel.oddsBoosterBanners,
  WSPublicChannel.realTimeIncomingBets
];
const MAX_UIDS = 1e3;
const seenUids = /* @__PURE__ */ new Map();
function isDuplicate(uid) {
  if (seenUids.has(uid)) {
    return true;
  }
  seenUids.set(uid, true);
  if (seenUids.size > MAX_UIDS) {
    const oldest = seenUids.keys().next().value;
    seenUids.delete(oldest);
  }
  return false;
}
function processAndEmit(channel, dataObj, observer) {
  const { uid, event, payload, src } = dataObj;
  if (uid && isDuplicate(uid)) {
    return;
  }
  if ((window.sseLogged || []).includes(event) || window.debugProto) {
    console.group(`${channel}: ${event}`);
    console.log("###event###", event, /* @__PURE__ */ new Date());
    console.log("###channel###", channel);
    console.log("###payload###", payload);
    console.log("###uid###", uid);
    console.log("###src###", src);
    console.groupEnd();
  }
  observer.next({ ...dataObj, channel });
}
function createOnMessage(observer, isProtoBuf) {
  return (channel, message) => {
    if (isProtoBuf) {
      import(
        /* webpackChunkName: "protobuf" */
        "./protobuf.js"
      ).then(({ decodeProtoBufMessage }) => {
        const dataObj = decodeProtoBufMessage(message);
        processAndEmit(channel, dataObj, observer);
      }).catch((err) => {
        console.error("error while loading protobuf chunk:", err);
      });
    } else {
      const dataObj = (message == null ? void 0 : message.data) ?? {};
      processAndEmit(channel, dataObj, observer);
    }
  };
}
const makeSubscription = ({
  api,
  namespace,
  payload,
  observer,
  keepInactive = false,
  isProtoBuf = false
}) => {
  api.subscribeAll({
    namespace,
    channels: payload,
    callback: createOnMessage(observer, isProtoBuf),
    isProtoBuf
  });
  if (!keepInactive) {
    api.unsubscribeInactive({
      namespace,
      channels: payload,
      isProtoBuf
    });
  }
};
const subscribeToEvents = (data, observer, params = {}) => {
  const { api, payload, namespace } = data;
  const { keepInactive } = params;
  makeSubscription({
    api,
    namespace,
    payload,
    observer,
    keepInactive,
    isProtoBuf: params == null ? void 0 : params.isProtoBuf
  });
};
const subscribeToPublic = (data, observer, params) => {
  const { api, namespace, payload } = data;
  makeSubscription({
    api,
    namespace,
    observer,
    payload,
    isProtoBuf: params == null ? void 0 : params.isProtoBuf
  });
};
const subscribeToUsers = (data, observer, params) => {
  const { api, namespace, payload } = data;
  const { token, customerId, customerToken } = payload || {};
  api.setToken(token, { Authorization: `Bearer ${customerToken}` });
  makeSubscription({
    api,
    namespace,
    observer,
    payload: [`common#${customerId}`],
    isProtoBuf: params == null ? void 0 : params.isProtoBuf
  });
};
const subscribeToRegistrationDeviceSwitcher = (data, observer, params) => {
  const { api, namespace, payload } = data;
  const { token, customerId } = payload || {};
  api.setToken(token, { "ws-session-uuid": `${customerId}` });
  makeSubscription({
    api,
    namespace,
    observer,
    payload: [`user-registration#${customerId}`],
    isProtoBuf: params == null ? void 0 : params.isProtoBuf
  });
};
const subscribeToCashouts = (data, observer, params) => {
  const { api, namespace, payload } = data;
  const { customerId } = payload || {};
  makeSubscription({
    api,
    namespace: WSNamespaces.users,
    observer,
    payload: [`${namespace}#${customerId}`, `common#${customerId}`],
    isProtoBuf: params == null ? void 0 : params.isProtoBuf
  });
};
const subscribeToLastSumUpdate = (data, observer, params) => {
  const { api, namespace, payload } = data;
  const { customerId } = payload || {};
  makeSubscription({
    api,
    namespace: WSNamespaces.public,
    observer,
    payload: [`${namespace}#${customerId}`],
    isProtoBuf: params == null ? void 0 : params.isProtoBuf
  });
};
const mappedSubscriptionTypes = {
  [WSNamespaces.public]: subscribeToPublic,
  [WSNamespaces.events]: subscribeToEvents,
  [WSNamespaces.users]: subscribeToUsers,
  [WSNamespaces.cashouts]: subscribeToCashouts,
  [WSNamespaces.seasons]: subscribeToEvents,
  [WSNamespaces.stages]: subscribeToEvents,
  [WSNamespaces.leagues]: subscribeToEvents,
  [WSNamespaces.lastBetSumUpdate]: subscribeToLastSumUpdate,
  [WSNamespaces.sessionSocket]: subscribeToRegistrationDeviceSwitcher
};
const fromWebSocket = (centrifuge, openObserver, params) => new Observable((observer) => {
  const { namespace, api } = centrifuge;
  mappedSubscriptionTypes[namespace](centrifuge, observer, params);
  return () => {
    api.unsubscribeAll(namespace);
  };
});
export {
  createOnMessage,
  fromWebSocket,
  publicEventsIds
};
